import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { extractLogo, validateLogoFile } from "@/lib/siteLogoHelpers";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const SETTING_KEY = "site_logo";

export function useSiteLogo() {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-settings", currentTenantId],
    queryFn: () =>
      portalFetch<Record<string, unknown>>(`/api/portal/tenants/${currentTenantId}/settings`),
    enabled: !!currentTenantId,
  });

  const logo = extractLogo(data?.[SETTING_KEY]);

  return { data: logo, isLoading };
}

export function useUploadSiteLogo() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      validateLogoFile(file);

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      return portalFetch(`/api/portal/tenants/${currentTenantId}/settings/${SETTING_KEY}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, key: SETTING_KEY, value: dataUrl }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-settings"] }),
  });
}

export function useDeleteSiteLogo() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/settings/${SETTING_KEY}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, key: SETTING_KEY, value: "" }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-settings"] }),
  });
}
